import axios from "@axios";

const DEFAULT_STATE = {
  applyFilters: false,
  applySearch: false,
  loading: false,
  categoryList: [],
  siteAreaList: [],
  siteBrandList: [],
  siteGroupList: [],
  siteList: [],
  partnerList: [],
  partnerViewGroupList: [],
  partnerViewList: [],
  departmentList: [],
  redac: [],
  initializedFromUrl: {},
  queryString: {},
  queryStringIsUpToDate: false,
  airflowDagList: [],
  lastMepTime: null,
  isSiteRestricted: JSON.parse(localStorage.getItem("isSiteRestricted") || "false"),
  accessibleSiteIds: JSON.parse(localStorage.getItem("accessibleSiteIds") || "[]"),
  capabilities: JSON.parse(localStorage.getItem("capabilities") || "{}"),
};

export default {
  namespaced: true,
  state: {
    ...DEFAULT_STATE,
  },
  getters: {
    getApplyFilters: (state) => state.applyFilters,
    getApplySearch: (state) => state.applySearch,
    getLoading: (state) => state.loading,
    getCategoryList: (state) => state.categoryList,
    getSiteAreaList: (state) => state.siteAreaList,
    getSiteBrandList: (state) => state.siteBrandList,
    getSiteGroupList: (state) => state.siteGroupList,
    getSiteList: (state) => state.siteList,
    getPartnerList: (state) => state.partnerList,
    getPartnerViewGroupList: (state) => state.partnerViewGroupList,
    getPartnerViewList: (state) => state.partnerViewList,
    getDepartmentList: (state) => state.departmentList,
    getRedacList: (state) => state.redac,
    getInitializedFromUrl: (state) => state.initializedFromUrl,
    getQueryString: (state) => state.queryString,
    getQueryStringIsUpToDate: (state) => state.queryStringIsUpToDate,
    getAirflowDagList: (state) => state.airflowDagList,
    getLastMepTime: (state) => state.lastMepTime,
    getIsSiteRestricted: (state) => state.isSiteRestricted,
    getAccessibleSiteIds: (state) => state.accessibleSiteIds,
    getCapabilities: (state) => state.capabilities,
    hasCapability: (state) => (key) =>
      Boolean(state.capabilities && state.capabilities[key]),
    getAccessibleSiteList: (state) =>
      state.isSiteRestricted && state.accessibleSiteIds.length
        ? state.siteList.filter((site) =>
            state.accessibleSiteIds.includes(site.id)
          )
        : state.siteList,
  },
  mutations: {
    initState(state) {
      for (const [key, value] of Object.entries(DEFAULT_STATE)) {
        state[key] = value;
      }
    },
    setApplyFilters(state, payload) {
      state.applyFilters = payload;
    },
    setApplySearch(state, payload) {
      state.applySearch = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    },
    setCategoryList(state, payload) {
      state.categoryList = payload;
    },
    setSiteAreaList(state, payload) {
      state.siteAreaList = payload;
    },
    setSiteBrandList(state, payload) {
      state.siteBrandList = payload;
    },
    setSiteGroupList(state, payload) {
      state.siteGroupList = payload;
    },
    setSiteList(state, payload) {
      state.siteList = payload;
    },
    setPartnerList(state, payload) {
      state.partnerList = payload;
    },
    setPartnerViewGroupList(state, payload) {
      state.partnerViewGroupList = payload;
    },
    setPartnerViewList(state, payload) {
      state.partnerViewList = payload;
    },
    setDepartmentList(state, payload) {
      state.departmentList = payload;
    },
    setRedacList(state, payload) {
      state.redac = payload;
    },
    setInitializedFromUrl(state, payload) {
      state.initializedFromUrl = payload;
    },
    setQueryString(state, payload) {
      state.queryString = payload;
    },
    setQueryStringIsUpToDate(state, payload) {
      state.queryStringIsUpToDate = payload;
    },
    setAirflowDagList(state, payload) {
      state.airflowDagList = payload;
    },
    mergeAirflowDagList(state, payload) {
      const existingIds = new Set(state.airflowDagList.map((d) => d.dag_id));
      const newItems = payload.filter((d) => !existingIds.has(d.dag_id));
      state.airflowDagList = [...state.airflowDagList, ...newItems];
    },
    setLastMepTime(state, payload) {
      state.lastMepTime = payload;
    },
    setIsSiteRestricted(state, payload) {
      state.isSiteRestricted = payload;
      localStorage.setItem("isSiteRestricted", JSON.stringify(payload));
    },
    setAccessibleSiteIds(state, payload) {
      state.accessibleSiteIds = payload || [];
      localStorage.setItem("accessibleSiteIds", JSON.stringify(payload || []));
    },
    setCapabilities(state, payload) {
      state.capabilities = payload || {};
      localStorage.setItem("capabilities", JSON.stringify(payload || {}));
    },
  },
  actions: {
    initState({ commit }) {
      commit("initState");
    },
    setIsSiteRestricted({ commit }, payload) {
      commit("setIsSiteRestricted", payload);
    },
    setAccessibleSiteIds({ commit }, payload) {
      commit("setAccessibleSiteIds", payload);
    },
    setCapabilities({ commit }, payload) {
      commit("setCapabilities", payload);
    },
    async fetchCapabilities({ commit }) {
      if (!localStorage.getItem("accessToken")) {
        return {};
      }
      const { data } = await axios.get("/users/capabilities");
      const capabilities = (data && data.capabilities) || {};
      commit("setCapabilities", capabilities);

      return capabilities;
    },
    updateApplyFilters({ commit }, payload) {
      return new Promise((resolve) => {
        if (payload === false) {
          setTimeout(() => {
            commit("setApplyFilters", payload);
            resolve();
          }, 1000);
        } else {
          commit("setApplyFilters", payload);
          resolve();
        }
      });
    },
    updateApplySearch({ commit }, payload) {
      commit("setApplySearch", payload);
    },
    updateLoading({ commit }, payload) {
      commit("setLoading", payload);
    },
    async getCategoryList({ commit }, payload) {
      const {
        data: { items },
      } = await axios.get("/adn-post-category/list", {
        params: payload,
      });
      commit("setCategoryList", items);

      return items;
    },
    async getSiteAreaList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/sites/areas/");
      commit("setSiteAreaList", items);

      return items;
    },
    async getSiteBrandList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/sites/brands/");
      commit("setSiteBrandList", items);

      return items;
    },
    async getSiteGroupList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/site-groups/", {
        params: { sort_by: "name" },
      });
      commit("setSiteGroupList", items);

      return items;
    },
    async getAccessibleSiteList({ dispatch }) {
      return dispatch("getSiteList");
    },
    async getSiteList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/sites/", {
        params: { sort_by: "name" },
      });
      commit("setSiteList", items);

      return items;
    },
    async getPartnerList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/partners/", {
        params: { order_by: "name" },
      });
      commit("setPartnerList", items);

      return items;
    },
    async getRedacList({ commit }, payload) {
      const items = await axios.get("/redacs/get_recent_redacs", {
        params: payload,
      }); // possibility to put a limit if needed  {params: { limit: None }}
      commit("setRedacList", items.data.res);

      return items.data.res;
    },
    async getPartnerViewGroupList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/partner-view-groups/", {
        params: { sort_by: "name" },
      });
      commit("setPartnerViewGroupList", items);

      return items;
    },
    async getPartnerViewList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/partner-views/", {
        params: { order_by: "label" },
      });
      commit("setPartnerViewList", items);

      return items;
    },
    async getDepartmentList({ commit }) {
      const { data } = await axios.get("/departments/");
      commit("setDepartmentList", data);

      return data;
    },
    setInitializedFromUrl({ commit, getters }, payload) {
      const keys = Object.keys(payload);
      if (!keys.includes("param") || !keys.includes("status")) {
        return;
      }
      commit("setInitializedFromUrl", {
        ...getters["getInitializedFromUrl"],
        [payload.param]: !!payload.status,
      });
    },
    isInitializingFromUrl({ getters }, payload) {
      return payload in getters["getInitializedFromUrl"];
    },
    isInitializedFromUrl({ getters }, payload) {
      return (
        payload in getters["getInitializedFromUrl"] &&
        getters["getInitializedFromUrl"][payload]
      );
    },
    initializeQueryString({ commit }, payload) {
      commit("setQueryString", payload);
      commit("setQueryStringIsUpToDate", true);
    },
    setQueryStringInitialized({ commit }) {
      commit("setQueryStringIsUpToDate", true);
    },
    addQueryStringParam({ commit, getters }, payload) {
      const keys = Object.keys(payload);
      if (!keys.includes("param") || !keys.includes("value")) {
        return;
      }
      commit("setQueryStringIsUpToDate", false);
      commit("setQueryString", {
        ...getters["getQueryString"],
        [payload.param]: payload.value,
      });
    },
    async collectAirflowDagList({ commit }, payload = {}) {
      if (!isAirflowEnabled()) return { items: [], total: 0 };

      const { data } = await axios.get("/airflow/dag/list", {
        params: {
          only_active: false,
          ...payload,
        },
      });
      const items = data && data.items ? data.items : [];
      const total = data && data.total !== undefined ? data.total : items.length;
      commit("setAirflowDagList", items);

      return { items, total };
    },
    async fetchAirflowDagsByIds({ commit }, dagIds) {
      if (!dagIds || !dagIds.length) {
        return [];
      }
      if (!isAirflowEnabled()) return [];

      try {
        const {
          data: { items },
        } = await axios.post("/airflow/dag/list", {
          dag_ids: dagIds,
        });
        commit("mergeAirflowDagList", items ?? []);

        return items ?? [];
      } catch (error) {
        console.error("Failed to fetch Airflow DAGs by IDs:", error);
        throw error;
      }
    },
    async updateLastMepTime({ commit }, payload) {
      commit("setLastMepTime", payload);
    },
  },
};

function isAirflowEnabled() {
  if (
    process.env.NODE_ENV !== "production" &&
    (!("VUE_APP_AIRFLOW_ENABLED" in process.env) ||
      process.env.VUE_APP_AIRFLOW_ENABLED !== "true")
  ) {
    return false;
  }
  return true;
}
